/**
 * Filter chip group used across the Sales widgets (RFPs, IO delivery,
 * recent activity). Each widget renders a row of mutually-exclusive chip
 * buttons; this consolidates the visual style and selection logic.
 */

import React from 'react';

interface ChipOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

interface Props<T extends string> {
  options: ChipOption<T>[];
  value: T;
  onChange: (next: T) => void;
  size?: 'sm' | 'md';
  /** 'soft' tints the selected chip instead of filling it, for filter rows
   *  that sit below a primary control and shouldn't outshout it. */
  tone?: 'solid' | 'soft';
}

function FilterChipGroup<T extends string>({ options, value, onChange, size = 'md', tone = 'solid' }: Props<T>) {
  const chipStyle = size === 'sm' ? styles.chipSm : styles.chipMd;
  const activeStyle = tone === 'soft' ? styles.activeSoft : styles.active;
  return (
    <div style={styles.row}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
          style={{ ...chipStyle, ...(value === opt.value ? activeStyle : {}) }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

const baseChip: React.CSSProperties = {
  fontWeight: 600,
  background: 'transparent',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--border-1)',
  borderRadius: 'var(--radius-pill)',
  cursor: 'pointer',
  color: 'var(--fg-3)',
  fontFamily: 'var(--font-sans)',
};

const styles: Record<string, React.CSSProperties> = {
  row: { display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' },
  chipMd: { ...baseChip, padding: '6px 10px', fontSize: 12 },
  chipSm: { ...baseChip, padding: '4px 8px', fontSize: 11 },
  active: { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' },
  activeSoft: { background: 'var(--accent-soft)', color: 'var(--accent-soft-fg)', borderColor: 'var(--border-2)' },
};

export default FilterChipGroup;
